import {TicketService} from "@share/common/services/ticket/TicketService";
import {Ticket} from "@api/models/tickets/Ticket";
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";
import Axios from "axios";
import i18n from "@service/locale/i18";
import {FilterSetting} from "@common/table/interfaces/FilterSetting";
import {TableColumn} from "@common/table/interfaces/TableColumn";

import {TableHelperInterface} from '@common/table/interfaces/TableHelper';
import {AbstractTableHelper} from '@common/table/AbstractTableHelper';
import {TicketType} from "@share/ticketSystem/enums/TicketTypes";
import {FilterType} from "@common/table/enums/FilterTypes";
import {TicketField} from "@share/ticketSystem/enums/TicketFields";
import { Store } from "vuex";
import { TicketStatusCode } from "../enums/TicketStatusCode";
import { ArrayHelpers } from "@common/ArrayHelpers";

export class TicketTableHelper extends AbstractTableHelper implements TableHelperInterface {
    ticketService: TicketService;
    list: Ticket[] = [];
    ticketType: TicketType;
    searchableFields: string[] = ['title', 'description'];
    onlyOwn = false; // запрашивать только свои тикеты в пользовательском режиме

    readonly TICKETS_QUERY_INCLUDE: string[] | Record<string, any> = [
        'id',
        'title',
        'ownerIds',
        'createdAt',
        'implementedAt',
        'isAnonymous',
        'responsibleUserId',
        { 'company': ['title'] },
        { 'status': ['title', 'color', 'iconFileId'] },
        { 'category': ['title'] }
    ]

    constructor(
        ticketService: TicketService,
        ticketType: TicketType,
        store: Store<any>
    ) {
        super(store);
        this.ticketService = ticketService;
        this.ticketType = ticketType;
    }

    get ticketStatusCode() {
        switch (this.ticketType) {
            case TicketType.idea:
                return TicketStatusCode.ideas
            case TicketType.problem:
                return TicketStatusCode.problems

            default:
                return null
        }
    }

    async getList() {
        this.isLoading = true;
        if (this.cancelToken) {
            this.cancelToken.cancel();
        }

        const query = this.getRequestQuery(this.TICKETS_QUERY_INCLUDE);

        this.cancelToken = Axios.CancelToken.source();
        try {
            const isFirstPage = this.page == 1;

            if (isFirstPage) {
                query.skip = 0;
                this.list = [];
            }

            const ticketsData = await this.ticketService.getTicketsWithCount(this.ticketType, query, this.cancelToken.token, this.onlyOwn);
            const tickets = ticketsData?.tickets || [];
            const usersResponse = await this.ticketService.getTicketsUsers(tickets, {}, this.cancelToken.token);
            const users = ArrayHelpers.keyBy(usersResponse, 'userId');

            tickets.forEach((ticket: Ticket) => {
                ticket.responsibleUser = null;
                ticket.owner = null;

                if (users) {
                    if (ticket.responsibleUserId &&  users[ticket.responsibleUserId]) {
                        ticket.responsibleUser = users[ticket.responsibleUserId];
                    }

                    if (ticket.ownerIds?.length && users[ticket.ownerIds[0]]) {
                        ticket.owner = users[ticket.ownerIds[0]];
                    }
                }
            });

            this.list = isFirstPage
                ? tickets
                : this.list.concat(this.filterDuplicates(tickets));
            this.count = ticketsData?.count || 0;

            if (this.sortOnClient) {
                await this.sortList();
            }
        } catch (e) {
            this.store.dispatch('reportException', e, {root: true});
        }
        finally {
            this.isLoading = false;
        }
    }

    getRequestQuery(include: string[] | Record<string, any>, usePagging = true): QueryParametersInterface {
        const where = this.getCondition();
        const order: any = this.getOrderSettings(
            this.filterDirection,
            this.filterOrder
        );

        return {
            ...(usePagging ? {
                take: this.take,
                skip: this.list.length,
            } : null),
            include: include,
            order,
            where
        };
    }

    private getCondition() {
        const where: { [key: string]: any } = this.searchStr.trim().length ? this.prepareFilter({ searchStr: this.searchStr }) : {};

        if (Object.keys(this.filters).length !== 0) {
            Object.keys(this.filters).forEach((fieldName) => {
                if (Array.isArray(this.filters[fieldName]) && this.filters[fieldName].length) {
                    where[fieldName] = { $in: this.filters[fieldName] };
                } else {
                    where[fieldName] = this.filters[fieldName];
                }
            });
        }

        return where;
    }

    async getFilterList() {
        let query = {
            skip: 0,
            take: -1,
            order: [
                {
                    column: 'title',
                    direction: 'asc'
                }
            ]
        };

        const [statuses, categories] = await Promise.all([
            this.ticketService.getTicketsRefCollections(
                TicketService.TICKET_STATUSES_COLLECTION,
                {
                    where: {
                        parentId: {
                            $inQuery: {
                                schema: TicketService.TICKET_STATUSES_COLLECTION,
                                where: {
                                    code: this.ticketStatusCode
                                }
                            }
                        }
                    },
                    ...query
                }
            ),
            this.ticketService.getTicketsRefCollections(
                TicketService.TICKET_CATEGORIES_COLLECTION,
                {
                    where: {
                        type: this.ticketType
                    },
                    ...query
                }
            )
        ]);

        let filterSettings: FilterSetting[] = [
            {
                type: FilterType.text,
                name: 'title',
                title: i18n.t('tickets.filters.title'),
                data: null,
                selectedItem: {},
                value: ''
            },{
                type: FilterType.dateRange,
                name: 'createdAt',
                title: i18n.t('tickets.filters.createdAt'),
                data: null,
                selectedItem: {},
                value: ''
            },{
                type: FilterType.select,
                name: 'company',
                title: i18n.t('tickets.filters.company'),
                data: null,
                ref: 'CompanyStructure',
                selectedItem: {}
            },
            {
                type: FilterType.select,
                name: 'branch',
                title: i18n.t('tickets.filters.branch'),
                data: null,
                ref: 'CompanyStructure',
                titleAddition: {
                    'parentId': ['title']
                },
                selectedItem: {},
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    title: i18n.t('tickets.filters.divisions'),
                    width: '166px',
                    sortingWeight: 50
                }
            },
            {
                type: FilterType.select,
                name: 'direction',
                title: i18n.t('tickets.filters.direction'),
                data: null,
                ref: 'CompanyStructure',
                titleAddition: {
                    'parentId': ['title']
                },
                selectedItem: {}
            },
            {
                type: FilterType.select,
                name: 'ownerId',
                title: i18n.t('tickets.filters.ownerIds'),
                data: null,
                ref: 'UserProfiles',
                selectedItem: {},
                isFieldMultiple: false,
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    width: '88px',
                    sortingWeight: 20
                }
            },
            {
                type: FilterType.select,
                name: 'responsibleUserId',
                title: i18n.t('tickets.filters.responsibleUserId'),
                data: null,
                ref: 'UserProfiles',
                selectedItem: {},
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    width: '161px',
                    sortingWeight: 10
                }
            },
            {
                type: FilterType.select,
                name: 'status',
                title: i18n.t('tickets.filters.status'),
                data: statuses?.map((item) => {
                    return {
                        id: item.id,
                        title: item.title
                    };
                }) || null,
                selectedItem: {},
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    width: '107px',
                    sortingWeight: 40
                },
                key: 'status' + this.ticketType
            },
            {
                type: FilterType.select,
                name: 'category',
                title: i18n.t('tickets.filters.category'),
                data: categories?.map((item) => {
                    return {
                        id: item.id,
                        title: item.title
                    };
                }) || null,
                selectedItem: {},
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    width: '123px',
                    sortingWeight: 30
                },
                key: 'category' + this.ticketType
            }
        ];

        if (this.ticketType === TicketType.problem) {
            filterSettings.splice(5, 0, {
                type: FilterType.select,
                name: 'responsibleForImplementationUserId',
                title: i18n.t('tickets.filters.responsibleForImplementationUserId'),
                data: null,
                ref: 'UserProfiles',
                selectedItem: {}
            });
        }

        return filterSettings;
    }

    getTableColumns(): TableColumn[] {
        let columns: TableColumn[] = [
            {
                fieldName: TicketField.title,
                filterable: true,
                label: i18n.t('tickets.columns.' + this.ticketType)
            },
            {
                fieldName: TicketField.category,
                filterable: false,
                class: 'not-cursor-on-hover'
            },
            {
                fieldName: TicketField.status,
                filterable: false,
                class: 'not-cursor-on-hover'
            },
            {
                fieldName: TicketField.company,
                filterable: false,
                class: 'column-hide-xl not-cursor-on-hover'
            },
            {
                fieldName: TicketField.responsibleUserId,
                filterable: false,
                class: 'not-cursor-on-hover'
            },
            {
                fieldName: TicketField.createdAt,
                filterable: true,
                type: 'DateTime',
                label: i18n.t('tickets.columns.dateOfSubmission')
            }
        ];

        if (this.ticketType === TicketType.idea) {
            columns.push({
                fieldName: 'implementedAt',
                filterable: true,
                type: 'DateTime',
                label: i18n.t('tickets.columns.implementationDate'),
                class: 'column-hide-xxl'
            });
        }
        return columns;
    }

    setOnlyOwn(value: boolean) {
        this.onlyOwn = value;
    }
}
